import { createContext, useContext, useState, useCallback, useRef } from 'react';
import { useClient } from './ClientContext';
import { setImpersonatedMspId } from '../api/apiClient';

const ImpersonationContext = createContext(null);
const STORAGE_KEY = 'impersonatedMsp';

function readStoredImpersonation() {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.mspId || !parsed?.mspName) return null;
    setImpersonatedMspId(parsed.mspId);
    return parsed;
  } catch {
    sessionStorage.removeItem(STORAGE_KEY);
    return null;
  }
}

export function ImpersonationProvider({ children }) {
  const [impersonatedMsp, setImpersonatedMsp] = useState(readStoredImpersonation);
  const activeMspId = useRef(impersonatedMsp?.mspId ?? null);
  const { setClientId } = useClient();

  const startImpersonation = useCallback((mspId, mspName) => {
    if (activeMspId.current !== mspId) setClientId(null);
    activeMspId.current = mspId;
    setImpersonatedMspId(mspId); // set module state synchronously so API calls include header immediately
    const next = { mspId, mspName };
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    setImpersonatedMsp(next);
  }, [setClientId]);

  const stopImpersonation = useCallback(() => {
    setClientId(null);
    activeMspId.current = null;
    setImpersonatedMspId(null);
    sessionStorage.removeItem(STORAGE_KEY);
    setImpersonatedMsp(null);
  }, [setClientId]);

  return (
    <ImpersonationContext.Provider value={{ impersonatedMsp, startImpersonation, stopImpersonation }}>
      {children}
    </ImpersonationContext.Provider>
  );
}

export function useImpersonation() {
  const ctx = useContext(ImpersonationContext);
  if (!ctx) return { impersonatedMsp: null, startImpersonation: () => {}, stopImpersonation: () => {} };
  return ctx;
}
